import { useClientPortalAPI } from "@certn/api";
import {
  Icon,
  Loader,
  mq,
  SectionMessage,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { layouts } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { CaseDocs } from "./CaseDocs";
import { Claims } from "./Claims";
import { Markdown } from "../Markdown";
import { useScrollToHashLocation } from "../hooks";

export function Checks() {
  const { apiConfigurationID = "" } = useParams();

  const { data, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/public-api/documentation/checks/`,
    {
      public_api_configuration: apiConfigurationID,
    }
  );
  useScrollToHashLocation(data);
  const { t } = useTranslation();

  if (isLoading) {
    return <Loader />;
  }
  if (error) {
    return (
      <SectionMessage
        sectionTitle={t("There was a problem loading API documentation")}
        type="danger"
      >
        {error}
      </SectionMessage>
    );
  }

  return (
    <>
      <Text as="p" className={styles.constrainedWidth}>
        <Trans>
          On this page you'll find the documentation for each of the different
          check types offered in the API to order.
        </Trans>
      </Text>
      {Object.entries(data?.case || {}).map(([key, value]) => (
        <CaseDocs
          key={key.replace(" ", "-")}
          {...value}
          testID={key.replace(" ", "-")}
        />
      ))}
      {data?.checks.map((check) => {
        return (
          <Stack
            data-testid={check.versioned_identifier}
            key={check.versioned_identifier}
            gap="space-2"
          >
            <Text
              as="h3"
              style="global/heading-medium"
              id={check.versioned_identifier}
            >
              {check.name} (<code>{check.versioned_identifier}</code>)
            </Text>
            <Text className={styles.constrainedWidth}>{check.description}</Text>
            <Stack
              orientation="horizontal"
              className={styles.constrainedWidth}
              gap="space-1"
            >
              <Text>{t("Orderable by invite:")}</Text>
              <Icon
                name={check.is_orderable_by_invite ? "check_circle" : "cancel"}
              />
            </Stack>
            <Stack
              orientation="horizontal"
              className={styles.constrainedWidth}
              gap="space-1"
            >
              <Text>{t("Orderable by quickscreen:")}</Text>
              <Icon
                name={
                  check.is_orderable_by_quickscreen ? "check_circle" : "cancel"
                }
              />
            </Stack>
            <Claims
              testID={`${check.versioned_identifier}-input-claims`}
              claims={check.input_claims as object}
              title={t("Input claims JSON schema")}
            />
            {check.check_arguments.invite_and_quickscreen_arguments_same ? (
              <Claims
                testID={`${check.versioned_identifier}-check-arguments`}
                claims={check.check_arguments.invite as object}
                title={t("Check arguments JSON schema")}
              />
            ) : (
              <>
                <Claims
                  testID={`${check.versioned_identifier}-invite-check-arguments`}
                  claims={check.check_arguments.invite as object}
                  title={t("Invite arguments JSON schema")}
                />
                <Claims
                  testID={`${check.versioned_identifier}-quickscreen-check-arguments`}
                  claims={check.check_arguments.quickscreen as object}
                  title={t("Quickscreen arguments JSON schema")}
                />
              </>
            )}
            <Claims
              testID={`${check.versioned_identifier}-output-claims`}
              claims={check.output_claims as object}
              title={t("Output claims JSON schema")}
            />
            {check.supplementary_information && (
              <Claims
                testID={`${check.versioned_identifier}-supplementary-information`}
                claims={check.supplementary_information as object}
                title={t("Supplementary information JSON schema")}
              />
            )}
            {check.api_documentation && (
              <Markdown
                testId={`${check.versioned_identifier}-markdown-documentation`}
              >
                {check.api_documentation || ""}
              </Markdown>
            )}
          </Stack>
        );
      })}
    </>
  );
}

const styles = {
  constrainedWidth: css({}, mq({ maxWidth: layouts.narrow.maxWidth })),
};
